"use client";

import Image from "next/image";
import React from "react";

/* ---------------- Types ---------------- */
type CardProps = {
  title: string;
  amount: string;

  // Desktop subtitle (single line)
  subtitleDesktop: string;
  // Mobile subtitle split so we can force a line break
  subtitleMobileTop: string; // e.g., "Work at Big 4 &"
  subtitleMobileBottom: string; // e.g., "220+ MNCs"

  // Desktop logo strip
  logoPanelDeskSrc: string;
  logoPanelDeskAlt: string;
  logoPanelDeskW: number;
  logoPanelDeskH: number;

  // Mobile logo strip
  logoPanelMobSrc: string;
  logoPanelMobAlt: string;
  logoPanelMobW: number;
  logoPanelMobH: number;
};

/* ---------------- Card ---------------- */
function SalaryCard({
  title,
  amount,
  subtitleDesktop,
  subtitleMobileTop,
  subtitleMobileBottom,
  logoPanelDeskSrc,
  logoPanelDeskAlt,
  logoPanelDeskW,
  logoPanelDeskH,
  logoPanelMobSrc,
  logoPanelMobAlt,
  logoPanelMobW,
  logoPanelMobH,
}: CardProps) {
  return (
    <div className="w-full bg-white relative mx-auto">
      <div className="mx-auto">
        <div className="relative rounded-[12px] border-[1px] border-[rgba(248,248,248,0.3)] bg-[#3399FF] backdrop-blur-[8.537px]">
          {/* Header */}
          <div className="px-1 md:px-6 pt-2 md:pt-3 text-white">
            <p className="text-xs md:text-lg font-semibold leading-4">
              {title}
            </p>
            <p className="text-[26px] md:text-[32px] font-bold tracking-tight leading-8">
              {amount}
            </p>
          </div>

          {/* Body */}
          <div className="m-2 md:m-3 rounded-[12px] bg-[#F8F8F8] border border-[#EDEDF0]">
            <div className="px-4 md:px-6 py-2 md:py-3">
              {/* Subtitle: desktop single line, mobile forced break */}
              <p className="text-center text-black text-xs md:text-lg font-medium md:block hidden">
                {subtitleDesktop}
              </p>
              <p className="text-center md:text-black text-[rgba(0,15,52,0.6)] text-xs font-medium md:hidden block">
                {subtitleMobileTop} <br className="md:hidden" />
                {subtitleMobileBottom}
              </p>

              {/* Logo strip (desktop) */}
              <div className="mt-4 md:mt-6 justify-center hidden md:flex">
                <Image
                  src={logoPanelDeskSrc}
                  alt={logoPanelDeskAlt}
                  width={logoPanelDeskW}
                  height={logoPanelDeskH}
                  className="h-12 w-auto object-contain"
                />
              </div>

              {/* Logo strip (mobile) */}
              <div className="mt-3 justify-center flex md:hidden">
                <Image
                  src={logoPanelMobSrc}
                  alt={logoPanelMobAlt}
                  width={logoPanelMobW}
                  height={logoPanelMobH}
                  className="w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function SalaryShowcase() {
  const cardsData: CardProps[] = [
    {
      title: "Avg. Salary in India",
      amount: "₹12 lakhs+",

      subtitleDesktop: "Work at 220+ MNCs",
      subtitleMobileTop: "Work at",
      subtitleMobileBottom: "220+ MNCs",

      // Desktop logos
      logoPanelDeskSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/20lakhs_desktop_cma.webp",
      logoPanelDeskAlt: "Deloitte, PwC, KPMG, EY",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      // Mobile logos (use your mobile-optimized strip here + sizes)
      logoPanelMobSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/20lakhs_mobile_cma.webp",
      logoPanelMobAlt: "Deloitte, PwC, KPMG, EY (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
    {
      title: "Avg. Salary in USA",
      amount: "₹50 lakhs+",

      subtitleDesktop: "Work at 350+ CPA firms",
      subtitleMobileTop: "Work at 350+",
      subtitleMobileBottom: "CPA firms",

      // Desktop logos
      logoPanelDeskSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/50lakhs_desktop_cma.webp",
      logoPanelDeskAlt: "RSM, BDO Alliance USA, Allinial Global, CPAmerica",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      // Mobile logos (use your mobile-optimized strip here + sizes)
      logoPanelMobSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/50lakhs_mobile_cma.webp",
      logoPanelMobAlt:
        "RSM, BDO Alliance USA, Allinial Global, CPAmerica (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
  ];

  return (
    <>
      {/* Heading */}
      <section className="w-full bg-white py-8 md:py-12">
        <div className="main_container mx-auto px-4 text-center">
          <h2
            className="text-center font-semibold leading-tight relative inline-block
              text-[20px] md:text-[32px]
              tracking-[-0.01em]"
          >
            <span className="text-black">Jobs at MNCs in </span>
            <span className="bg-[linear-gradient(91deg,#39F_81.57%,#FFBA08_106.77%)] bg-clip-text text-transparent pr-[2px]">
              India &amp; U.S.
            </span>

            {/* underline */}
            <span
              aria-hidden
              className="pointer-events-none absolute md:left-[100px] left-[20px] md:right-[145px] right-[70px]
                -bottom-[4px] h-[2px] bg-[#FFB801] rounded-[5px] z-10"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-[4px] md:right-[115px] right-[41px]
                h-[2px] w-6 bg-[#FFB801] rounded-[5px] z-10"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-[4px] md:right-[95px] right-[20px]
                h-[2px] w-4 bg-[#FFB801] rounded-[5px] z-10"
            />
          </h2>

          {/* Cards */}
          <div className="grid grid-cols-2 gap-2 md:gap-7 md:w-[88%] mx-auto md:mt-10 mt-7">
            {cardsData.map((card, i) => (
              <SalaryCard key={i} {...card} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
